import { getSpreadDimensions } from '../../components/BookDesign/constants';
import { doesElementOverlapPage } from '../../utils/elementUtils';

export function getPageElements(book, pageNumber) {
  if (!book?.spreads) {
    console.warn('No spreads data available in book object');
    return [];
  }

  const dimensions = getSpreadDimensions(book.pageSize);
  const pageWidth = dimensions.width;
  
  // Special handling for cover pages
  const isCoverPage = pageNumber === 0 || pageNumber === parseInt(book.totalPages, 10) + 1;
  
  // Calculate which spread contains this page
  const spreadNumber = isCoverPage ? 0 : Math.ceil(pageNumber / 2);
  const spreadId = `spread-${spreadNumber}`;
  
  console.log(`Finding elements for page ${pageNumber} from spread ${spreadId}`);
  
  const spread = book.spreads[spreadId];
  if (!spread) {
    console.warn(`Spread ${spreadId} not found for page ${pageNumber}. Available spreads:`, Object.keys(book.spreads));
    return [];
  }
  
  if (!spread.elements || !Array.isArray(spread.elements)) {
    console.warn(`No elements array in spread ${spreadId}`, spread);
    return [];
  }
  
  // Determine if we're looking at a right or left page
  // For covers: page 0 = front cover (right), totalPages+1 = back cover (left)
  // For content: even pages are right, odd pages are left
  const isRightPage = pageNumber === 0 || (!isCoverPage && pageNumber % 2 === 0);
  
  console.log(`Page ${pageNumber} is on the ${isRightPage ? 'right' : 'left'} side of spread ${spreadId}`);
  console.log(`Found ${spread.elements.length} total elements in spread ${spreadId}`);

  // NEW APPROACH: Use direct page mapping first (leftPageElements/rightPageElements) if available
  if (isRightPage && spread.rightPageElements && spread.rightPageElements.length > 0) {
    console.log(`Using dedicated rightPageElements array with ${spread.rightPageElements.length} elements`);
    return spread.rightPageElements.map(el => ({
      ...el,
      position: {
        ...el.position,
        // No need to adjust X position for dedicated page elements
        x: el.position.x
      }
    }));
  }
  
  if (!isRightPage && spread.leftPageElements && spread.leftPageElements.length > 0) {
    console.log(`Using dedicated leftPageElements array with ${spread.leftPageElements.length} elements`);
    return spread.leftPageElements.map(el => ({
      ...el,
      position: {
        ...el.position,
        // No need to adjust X position for dedicated page elements
        x: el.position.x
      }
    }));
  }

  // FALLBACK: If no dedicated page elements, filter from combined elements array
  console.log(`Using element filtering for page ${pageNumber} based on position`);
  
  // Filter elements based on their horizontal position in the spread
  const pageElements = spread.elements
    .filter(element => {
      // Get element position and size
      const elementLeft = element.position?.x || 0;
      const elementRight = elementLeft + (element.size?.width || 0);
      
      // For right pages (including front cover), include elements that:
      // - Extend past the page boundary (spans into right page)
      // - This includes spanning elements that start on the left side
      if (isRightPage) {
        const belongs = elementRight > pageWidth;
        console.log(`Element ${element.id || 'unknown'} (left: ${elementLeft}, right: ${elementRight}, pageWidth: ${pageWidth}) ${belongs ? 'BELONGS' : 'does not belong'} on RIGHT page ${pageNumber}`);
        return belongs;
      }
      
      // For left pages (including back cover), include elements that:
      // - Start before the page boundary (includes spanning elements)
      else {
        const belongs = elementLeft < pageWidth;
        console.log(`Element ${element.id || 'unknown'} (left: ${elementLeft}, right: ${elementRight}, pageWidth: ${pageWidth}) ${belongs ? 'BELONGS' : 'does not belong'} on LEFT page ${pageNumber}`);
        return belongs;
      }
    })
    .map(element => {
      return {
        ...element,
        position: {
          ...element.position,
          // For right pages, adjust X position to bring elements to the origin
          x: isRightPage ? element.position.x - pageWidth : element.position.x
        }
      };
    });
  
  console.log(`Returning ${pageElements.length} elements for page ${pageNumber}`);
  return pageElements;
}